import React from 'react';
import { Route, Switch } from 'react-router-dom';
import { StripeCapitalSplash } from 'slickpay/capital/StripeCapitalSplash.component';
import SlickPayLayout from 'slickpay/no-reader/SlickPayLayout.component';
import AddTagsComponent from 'tags/AddTagsLayout.component';

import SupportPage from 'components/account/SupportPage.component';
import CashupPage from 'components/cashup/CashupPage.component';
import { CheckoutShell } from 'components/checkout/CheckoutSalonTaxesAndFeesSync.component';
import PinLock from 'components/core/PinLock.component';
import CacheBusterWatcher from 'components/core/watchers/CacheBusterWatcher.component';
import IntercomWatcher from 'components/core/watchers/IntercomWatcher.component';
import LoginWatcher from 'components/core/watchers/LoginWatcher.component';
import PageReloadWatcher from 'components/core/watchers/PageReloadWatcher.component';
import PinSettingsWatcher from 'components/core/watchers/PinSettingsWatcher.component';
import PinWatcher from 'components/core/watchers/PinWatcher.component';
import SalonUpdateWatcher from 'components/core/watchers/SalonUpdateWatcher.component';
import SalonWatcher from 'components/core/watchers/SalonWatcher.component';
import HumanResources from 'components/hr/HumanResources.component';
import StaffReportsRoutes from 'components/staffreps/StaffReportsRoutes.component';
import WebsitesPortfolio from 'components/websites/Portfolio.component';
import FLAG from 'config/flag.singleton';
import LINK from 'constants/link.const';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { fromPropsToIsWeek } from 'utils/converters/props/path.util';
import linkTo from 'utils/links/linkTo.util';

import ActivityWatcher from 'activity/watcher';
import Checkout from 'checkout/components/Checkout';
import CheckoutPaymentPopUpV1 from 'checkout/components/CheckoutPaymentPopUpV1.component';
import CheckoutPaymentPopUpV2 from 'checkout/components/CheckoutPaymentPopUpV2.component';
import SendReceiptAfterCheckout from 'checkout/components/SendReceiptAfterCheckout.component';
import ClientCommunicationsRoutes from 'clientCommunications/ClientCommunicationsRoutes.component';
import SalonRoute from 'lib/SalonRoute';
import ServicePriceList from 'price/ServicePriceList';
import Reports from 'reports/Reports';
import SchedulerLoader from 'scheduler/SchedulerLoader.component';
import BalanceSheetPage from 'settings/balance-sheet/BalanceSheetPage.component';
import SendReceiptAfterDeposit from 'settings/balance-sheet/SendReceiptAfterDeposit.component';
import OnlineBookingPage from 'settings/online-booking/OnlineBookingPage.component';
import Settings from 'settings/Settings';
import { StocksRoutes } from 'stocks/routes';
import Landing from 'user/common/Landing';
import VouchersRoutes from 'vouchers/routes';

const CheckoutRoutes = (props) => {
  const salonId = useCurrentSalonId();

  const CheckoutPaymentPopUp = 1 === FLAG.checkoutPaymentPopUp ? CheckoutPaymentPopUpV1 : CheckoutPaymentPopUpV2;

  const day = linkTo({ pattern: LINK.checkoutDay, params: { salonId } });
  const week = linkTo({ pattern: LINK.checkoutWeek, params: { salonId } });

  const prefix = fromPropsToIsWeek(props) ? week : day;

  // NOTE: this is used to pick routes elsewhere:
  // ? linkTo({ pattern: LINK.checkoutFinishWeek, params: { salonId, stylistId } })
  // : linkTo({ pattern: LINK.checkoutFinishDay, params: { salonId } }),

  // add new client needs props
  return (
    <>
      <Route path={day} render={(props) => <CheckoutShell>{({ taxes, fees }) => <Checkout taxes={taxes} fees={fees} {...props} />}</CheckoutShell>} />
      <Route path={`${prefix}/:stylistId/checkout`} render={(props) => <CheckoutShell>{({ taxes, fees }) => <Checkout taxes={taxes} fees={fees} {...props} />}</CheckoutShell>} />
      <Route path={`${prefix}/finish`} render={(props) => <CheckoutShell>{({ taxes, fees }) => <CheckoutPaymentPopUp taxes={taxes} fees={fees} {...props} />}</CheckoutShell>} />
      <Route
        path={`${prefix}/:stylistId/${LINK.SEG.checkoutFinish}`}
        render={(props) => <CheckoutShell>{({ taxes, fees }) => <CheckoutPaymentPopUp taxes={taxes} fees={fees} {...props} />}</CheckoutShell>}
      />
      <Route path={`${prefix}/complete`} component={SendReceiptAfterCheckout} />
      <Route path={`${prefix}/:stylistId/${LINK.SEG.checkoutComplete}`} component={SendReceiptAfterCheckout} />
    </>
  );
};

const PinLockedRoutes = () => (
  <>
    <Route path={LINK.sidSalon} component={CacheBusterWatcher} />
    <Route path={LINK.sidSalon} component={IntercomWatcher} />
    <Route path={LINK.sidSalon} component={LoginWatcher} />
    <Route path={LINK.sidSalon} component={PageReloadWatcher} />
    <Route path={LINK.sidSalon} component={PinWatcher} />
    <Route path={LINK.sidSalon} component={PinSettingsWatcher} />
    <Route path={LINK.sidSalon} component={SalonUpdateWatcher} />
    <Route path={LINK.sidSalon} component={SalonWatcher} />

    <PinLock>
      {/* does fetch for notifications feed which is behind pin lock */}
      <Route path={LINK.sidSalon} component={ActivityWatcher} />
    </PinLock>
    <Switch>
      {/* contains deeply nested component that has issue with re-rendering under PinLock */}
      <SalonRoute path="/salon/:id/settings" component={Settings} />
      <PinLock hasSwitch>
        <SalonRoute path="/salon/:id/reports" component={Reports} />
        <SalonRoute path="/salon/:id/landing" component={Landing} />
        <SalonRoute path="/salon/:id/day/checkout" component={CheckoutRoutes} />
        <SalonRoute path="/salon/:id/week/:stylistId/checkout" component={CheckoutRoutes} />
        <SalonRoute path="/salon/:id/stocks" component={StocksRoutes} />
        <SalonRoute path="/salon/:id/communications" component={ClientCommunicationsRoutes} />
        <SalonRoute path="/salon/:id/deposits/send_receipt" component={SendReceiptAfterDeposit} />
        <SalonRoute path="/salon/:id/deposits" component={BalanceSheetPage} />
        <SalonRoute path="/salon/:id/price-list" component={ServicePriceList} />
        <SalonRoute path={LINK.portfolio} component={WebsitesPortfolio} />
        <SalonRoute path={LINK.support} component={SupportPage} />
        <SalonRoute path={LINK.sidTags} component={AddTagsComponent} />
        <SalonRoute path="/salon/:id/hr" component={HumanResources} />
        <SalonRoute path="/salon/:id/slickpay" component={SlickPayLayout} />
        <SalonRoute path={LINK.stripeCapitalSplash} component={StripeCapitalSplash} />
        <SalonRoute path="/salon/:id/onlinebooking" component={OnlineBookingPage} />
        <SalonRoute path="/salon/:id/vouchers" component={VouchersRoutes} />
        <SalonRoute path="/salon/:id/cashup" component={CashupPage} />

        <SalonRoute path="/salon/:id/day/:dateString?/" component={SchedulerLoader} />
        <SalonRoute path="/salon/:id/week/:dateString?/:stylistId" component={SchedulerLoader} />

        <SalonRoute path="/salon/:id/day/:dateString?/*" component={SchedulerLoader} />
        <SalonRoute path="/salon/:id/week/:dateString?/:stylistId/*" component={SchedulerLoader} />
        <SalonRoute path={LINK.sidStaffReports} component={StaffReportsRoutes} />
      </PinLock>
    </Switch>
  </>
);

// noinspection JSUnusedGlobalSymbols
export default PinLockedRoutes;
